// Import CSS first to ensure styles are loaded before React hydration
import './application.css'

import { createInertiaApp, type ResolvedComponent } from '@inertiajs/react'
import { StrictMode, type ReactNode } from 'react'
import { hydrateRoot, createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { FlashToastHandler } from '@/components/FlashToastHandler'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { rememberFirstTouch } from '@/lib/attribution'

// Initialize Sentry for frontend error reporting
// Only initializes if VITE_SENTRY_DSN is set
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    // Capture 10% of transactions for performance monitoring in production
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Capture replays on errors
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: import.meta.env.PROD ? 1.0 : 0,
    // Don't send errors in development unless explicitly enabled
    enabled: import.meta.env.PROD || import.meta.env.VITE_SENTRY_ENABLED === 'true',
  })
}

// Note the visit's landing page before any Inertia navigation can change it
rememberFirstTouch()

// Marker to prevent duplicate layout wrapping (can happen with HMR)
const LAYOUT_WRAPPED = Symbol.for('inertia-layout-wrapped')

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  title: title => title || 'Osher Digital',

  // Brand-orange progress bar (Inertia's default is blue)
  // see https://inertia-rails.dev/guide/progress-indicators
  progress: { color: '#F99406' },

  resolve: async (name) => {
    // Lazy glob (no `eager`) so Vite code-splits one chunk per page. A
    // marketing visitor no longer downloads the admin/dashboard/settings
    // pages; each chunk loads on first visit. SSR (ssr.tsx) stays eager —
    // the server renders any page on any request and startup cost is paid once.
    const pages = import.meta.glob<{default: ResolvedComponent}>('../pages/**/*.tsx')

    const loader = pages[`../pages/${name}.tsx`]

    if (!loader) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    const page = await loader()

    // Wrap all pages with FlashToastHandler for toast notifications
    // Use marker to prevent duplicate wrapping (can happen with HMR or SSR hydration)
    // see https://inertia-rails.dev/guide/pages#default-layouts
    const component = page.default as ResolvedComponent & { [LAYOUT_WRAPPED]?: boolean }
    if (!component[LAYOUT_WRAPPED]) {
      // Inertia v3 types `layout` as component(s) or a layout function, and the
      // union isn't callable as-is. This app only ever assigns layout *functions*
      // (page: ReactNode) => ReactNode, so the call below is safe.
      const existingLayout = component.layout as ((page: ReactNode) => ReactNode) | undefined
      component.layout = (pageContent: ReactNode) => (
        <>
          <FlashToastHandler />
          {existingLayout ? existingLayout(pageContent) : pageContent}
        </>
      )
      component[LAYOUT_WRAPPED] = true
    }

    // An async resolver must yield the component itself (the sync form could
    // return the whole module).
    return component
  },

  setup({ el, App, props }) {
    const content = (
      <StrictMode>
        <ErrorBoundary>
          <TooltipProvider>
            <App {...props} />
            <Toaster position="top-right" richColors />
          </TooltipProvider>
        </ErrorBoundary>
      </StrictMode>
    )

    // Use hydration if SSR was used (element has pre-rendered content)
    const hasSSRContent = el.hasAttribute('data-server-rendered') || el.innerHTML.trim()
    if (hasSSRContent) {
      // Remove leading/trailing whitespace text nodes that can cause hydration mismatches
      // SSR output may have whitespace between container and content due to template formatting
      while (el.firstChild && el.firstChild.nodeType === Node.TEXT_NODE && !el.firstChild.textContent?.trim()) {
        el.removeChild(el.firstChild)
      }
      while (el.lastChild && el.lastChild.nodeType === Node.TEXT_NODE && !el.lastChild.textContent?.trim()) {
        el.removeChild(el.lastChild)
      }
      hydrateRoot(el, content)
    } else {
      createRoot(el).render(content)
    }
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
